import axios, { AxiosRequestConfig } from 'axios';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';

import ENV_VARIABLES from '../config';
import type { DrawingRegistration } from '../Utils/HelperFunctions/drawingUpload';
import { base64ToArrayBuffer } from '../Utils/HelperFunctions/helperFunctions';
import { pdfLocationText } from '../Utils/HelperFunctions/mediaLocation';
import {
  CameraLinkDocumentType,
  CameraLinkPublicType,
  CustomDocumentDataType,
  CustomDrawingDataType,
  CustomOrganisationDataType,
  CustomProjectDataType,
  CustomStatusDataType,
  CustomUserDataType,
  OrganisationType,
  ProjectDataType,
  StatusDataType,
  UserDataType,
  WithFID
} from '../Utils/types';

import { FirebaseService } from './Firebase';

const firebaseService = FirebaseService;

// Determine the base URL for API calls
// When running locally (dev server), always use proxy to avoid CORS
// When running in production build, use the configured URL based on VITE_APP_ENV
const getBaseURL = () => {
  // import.meta.env.DEV is true when running vite dev server (regardless of --mode)
  const isDevServer = import.meta.env.DEV;

  // When running locally (dev server), always use proxy to avoid CORS
  // This works regardless of VITE_APP_ENV (staging or production)
  if (isDevServer) {
    // Use relative path so Vite proxy can intercept it
    // This avoids CORS issues when running locally
    // Verify in Network tab: Request URL should be http://localhost:3000/api/...
    return '/api';
  }

  // Production build: use the configured URL based on VITE_APP_ENV
  // (staging or production, determined by config.ts)
  const functionsUrl = ENV_VARIABLES.functionsBaseUrl;
  if (!functionsUrl) {
    const errorMsg =
      '❌ functionsBaseUrl is not set. API calls will fail.\n' +
      `   Current app environment: ${import.meta.env.VITE_APP_ENV || 'development'}\n` +
      `   Expected variable: ${
        import.meta.env.VITE_APP_ENV === 'production' ? 'VITE_FUNCTIONS_BASE_URL' : 'VITE_STAGING_FUNCTIONS_BASE_URL'
      }\n` +
      '   Please set this variable in your environment or .env file.';

    if (import.meta.env.PROD) {
      // In production build, throw error (fail fast)
      throw new Error(errorMsg);
    } else {
      // In dev, warn but allow fallback
      console.error(errorMsg);
      return '/api'; // Fallback
    }
  }
  return functionsUrl;
};

const client = axios.create({ baseURL: getBaseURL() });

/**
 * Helper to build Authorization header only (no Content-Type)
 * @param requireAuth - If true, throws error if token is missing (prevents unauthenticated requests)
 * @returns Object with Authorization header if token is available, empty object otherwise
 */
const buildAuthHeader = async (requireAuth: boolean = true): Promise<Record<string, string>> => {
  const token = await firebaseService.auth.currentUser?.getIdToken();
  if (!token && requireAuth) {
    throw new Error('Missing auth token');
  }
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Helper to merge auth headers with existing config
 * Centralizes the pattern of merging headers so auth always wins
 * @param config - Optional AxiosRequestConfig to merge with
 * @param requireAuth - If true, throws error if token is missing (default: true)
 * @param includeJsonContentType - If true, includes 'Content-Type: application/json' (default: true)
 *                                 Set to false for multipart/form-data or other content types
 */
const withAuthConfig = async (
  config?: AxiosRequestConfig,
  requireAuth: boolean = true,
  includeJsonContentType: boolean = true
): Promise<AxiosRequestConfig> => {
  const authHeader = await buildAuthHeader(requireAuth);

  return {
    ...config,
    headers: {
      ...(includeJsonContentType ? { 'Content-Type': 'application/json' } : {}),
      ...(config?.headers ?? {}),
      ...authHeader // auth wins
    }
  } as AxiosRequestConfig;
};

/**
 * Can pass organisationId as filter params along AxiosRequestConfig to filter users by organisation ID
 * @param {String} organisationId
 * @returns
 */
export const getAllUsers = async (config?: AxiosRequestConfig): Promise<CustomUserDataType[]> => {
  const { data } = await client.get<CustomUserDataType[]>(`/v1/user`, await withAuthConfig(config));
  return data;
};

export const getUser = async (id: string) => {
  const { data } = await client.get<CustomUserDataType>(`/v1/user/${id}`, await withAuthConfig());
  return data;
};

export const createUserByEmail = async (user: UserDataType) => {
  const { data } = await client.post<CustomUserDataType>('/v2/user', { userData: { ...user } }, await withAuthConfig());
  return data;
};

export const editUser = async (user: Partial<UserDataType>, id: string) => {
  const { data } = await client.put<CustomUserDataType>(
    `/v1/user/${id}`,
    { user: { ...user } },
    await withAuthConfig()
  );
  return data;
};

export const deleteUser = async (id: string) => {
  const { data } = await client.delete<CustomUserDataType>(`/v1/user/${id}`, await withAuthConfig());
  return data;
};

// Public: the invitee is not signed in yet, so no token (OQ-62).
export const getInvite = async (id: string) => {
  const { data } = await client.get<{ email?: string }>(`/v1/user/${id}/invite`);
  return data;
};

// Sends the invitee's chosen name and password, which the server hands to Auth;
// answers only the id since OQ-158 (a public route).
export const registerUser = async (user: { displayName: string; password: string }, id: string) => {
  const { data } = await client.patch<{ docId: string }>(
    `/v1/user/${id}`,
    { user: { ...user } },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return data;
};

export const getOrganisation = async (id: string) => {
  try {
    const { data } = await client.get<CustomOrganisationDataType>(`/v1/org/${id}`, await withAuthConfig());
    if (!data?.docId) {
      console.warn('⚠️ getOrganisation: Response missing docId', { id, data });
    }
    return data;
  } catch (error) {
    const e = error as { response?: { data?: unknown }; message?: string };
    console.error('❌ getOrganisation failed:', e?.response?.data || e?.message || error);
    throw error;
  }
};

export const getDefaultOrganisation = async () => {
  try {
    const { data } = await client.get<CustomOrganisationDataType>(`/v1/default-org`, {
      headers: { 'Content-Type': 'application/json' }
    });
    if (!data?.docId) {
      console.warn('⚠️ getDefaultOrganisation: Response missing docId', data);
    }
    return data;
  } catch (error) {
    const e = error as { response?: { data?: unknown }; message?: string };
    console.error('❌ getDefaultOrganisation failed:', e?.response?.data || e?.message || error);
    throw error;
  }
};

export const getAllOrganisations = async () => {
  const { data } = await client.get<CustomOrganisationDataType[]>(`/v1/org`, await withAuthConfig());
  return data;
};

export const getOrganisationByUrlId = async (id: string) => {
  const { data } = await client.get<CustomOrganisationDataType>(`/v1/org/from-url/${id}`, await withAuthConfig());
  return data;
};

export const getCameraLinkDoc = async (id: string) => {
  const { data } = await client.get<CameraLinkPublicType>(`/v1/camera-link/${id}`, {
    headers: { 'Content-Type': 'application/json' }
  });
  return data;
};

export const createOrganisation = async (org: OrganisationType) => {
  const { data } = await client.post<CustomOrganisationDataType>(
    '/v1/org',
    { org: { ...org } },
    await withAuthConfig()
  );
  return data;
};

export const editOrganisation = async (org: Partial<OrganisationType>, id: string) => {
  const { data } = await client.put<CustomOrganisationDataType>(
    `/v1/org/${id}`,
    { org: { ...org } },
    await withAuthConfig()
  );
  return data;
};

export const assignAdminMessageToMedia = async (message: string, mediaId: string, organisationId?: string) => {
  const { data } = await client.put<CustomDocumentDataType>(
    `/v1/media/${mediaId}/admin-message`,
    { adminMessage: message, orgId: organisationId },
    await withAuthConfig()
  );
  return data;
};

export const assignStatusToMedia = async (statusId: string, mediaId: string, organisationId?: string) => {
  const { data } = await client.put<CustomDocumentDataType>(
    `/v1/media/${mediaId}/status`,
    { statusId, orgId: organisationId },
    await withAuthConfig()
  );
  return data;
};

export const removeStatusFromMedia = async (mediaId: string) => {
  const { data } = await client.delete<CustomDocumentDataType>(`/v1/media/${mediaId}/status`, await withAuthConfig());
  return data;
};

export const assignIsReadStatusToMedia = async (isRead: boolean, mediaId: string, organisationId?: string) => {
  const { data } = await client.put<CustomDocumentDataType>(
    `/v1/media/${mediaId}/read`,
    { isRead, orgId: organisationId },
    await withAuthConfig()
  );
  return data;
};

export const assignUserToMedia = async (
  userId: string,
  mediaId: string,
  organisationId: string
): Promise<CustomDocumentDataType> => {
  const { data } = await client.put<CustomDocumentDataType>(
    `/v1/media/${mediaId}/assignee`,
    { orgId: organisationId, userId },
    await withAuthConfig()
  );
  return data;
};

export const removeAssigneeFromMedia = async (mediaId: string) => {
  const { data } = await client.delete<CustomDocumentDataType>(`/v1/media/${mediaId}/assignee`, await withAuthConfig());
  return data;
};

export const deleteOrganisation = async (id: string) => {
  const authConfig = await withAuthConfig();
  const { data } = await client.delete(`/v1/org/${id}`, authConfig);
  return data;
};

export const getBackups = async () => {
  const { data } = await client.get('/v1/backups', await withAuthConfig());
  return data;
};

export const restoreBackup = async (id: string, dateString: string) => {
  const { data } = await client.post(
    '/v1/restore-backup',
    { organisationId: id, date: dateString },
    await withAuthConfig()
  );
  return data;
};

export const downloadPDFReport = async (organisationId: string, content: CustomDocumentDataType | string) => {
  try {
    const { data } = await client.get(
      '/v1/pdf-report',
      await withAuthConfig({
        params: {
          organisationId,
          data:
            typeof content === 'string'
              ? { fileName: content }
              : {
                  fileName: content.fileName,
                  docId: content.docId,
                  adminMessage: content.adminMessage,
                  message: content.message,
                  location: pdfLocationText(content),
                  timestamp: format(content.timestamp.toDate(), 'yyyy-MM-dd, HH:mm:ss', { locale: sv }),
                  // The report names the Drawing a pinned photo is on (OQ-161).
                  ...(content.drawingPin
                    ? { drawingPin: { drawingId: content.drawingPin.drawingId, page: content.drawingPin.page } }
                    : {})
                }
        }
      })
    );

    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(new Blob([base64ToArrayBuffer(data)]));
    a.download = 'rapport.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (error) {
    console.log(error);
  }
};

export const downloadAsPDF = async (organisationId: string, fileName: string) => {
  try {
    const { data } = await client.get(
      '/v1/pdf',
      await withAuthConfig({
        params: {
          organisationId,
          fileName
        }
      })
    );

    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(new Blob([base64ToArrayBuffer(data)]));
    a.download = `${fileName.split('.')[0]}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (error) {
    console.log(error);
  }
};

export const uploadMediaByOrgId = async (orgId: string, cameraId: string, form: FormData) => {
  // For multipart/form-data, omit Content-Type (let axios set boundary automatically)
  // This endpoint appears to be public (no auth), but if it should require auth, use:
  // const authHeader = await buildAuthHeader(true);
  // headers: { ...authHeader }
  const { data } = await client.post<CustomDocumentDataType>(`/v1/media/${orgId}/${cameraId}`, form, {
    // Omit Content-Type to let axios set boundary automatically for FormData
    // If backend requires explicit multipart, uncomment:
    // headers: { 'Content-Type': 'multipart/form-data' }
  });
  return data;
};

export const getFilePublicUrl = async (orgId: string, fileName: string) => {
  const { data } = await client.get<string>(
    '/v1/public-url',
    await withAuthConfig({ params: { folder: orgId, fileName } })
  );
  return data;
};

export const generateCameraLink = async (
  orgId: string,
  permanent: boolean = false
): Promise<CameraLinkDocumentType & WithFID> => {
  const { data } = await client.post('/v1/camera-link', { organisationId: orgId, permanent }, await withAuthConfig());
  return data;
};

// Revokes a permanent camera link and answers its replacement (OQ-115).
export const replaceCameraLink = async (linkId: string): Promise<CameraLinkDocumentType & WithFID> => {
  const { data } = await client.post(`/v1/camera-link/${linkId}/replace`, {}, await withAuthConfig());
  return data;
};

export const generateProjectCameraLink = async (
  organisationId: string,
  projectId: string,
  permanent: boolean = false
): Promise<CameraLinkDocumentType & WithFID> => {
  const { data } = await client.post(
    '/v1/camera-link-project',
    { organisationId, projectId, permanent },
    await withAuthConfig()
  );
  return data;
};

export const getAllProjects = async (config?: AxiosRequestConfig): Promise<CustomProjectDataType[]> => {
  const { data } = await client.get<CustomProjectDataType[]>('/v1/projects', await withAuthConfig(config));
  return data;
};

export const deleteProject = async (projectId: string): Promise<void> => {
  await client.delete(`/v1/projects/${projectId}`, await withAuthConfig());
};

export const deleteMedia = async (organisationId: string, mediaId: string): Promise<void> => {
  await client.delete(`/v1/media/${organisationId}/${mediaId}`, await withAuthConfig());
};

export const updateProject = async (projectId: string, payload: ProjectDataType): Promise<CustomProjectDataType> => {
  const { data } = await client.patch<CustomProjectDataType>(
    `/v1/projects/${projectId}`,
    payload,
    await withAuthConfig()
  );
  return data;
};

export const createProject = async (payload: ProjectDataType): Promise<CustomProjectDataType> => {
  const { data } = await client.post<CustomProjectDataType>(`/v1/projects`, payload, await withAuthConfig());
  return data;
};

export const addProjectToFile = async (projectId: string, fileId: string, organisationId: string) => {
  const { data } = await client.post<CustomProjectDataType>(
    `/v1/projects/${projectId}/${fileId}/${organisationId}`,
    {},
    await withAuthConfig()
  );
  return data;
};

export const removeProjectFromFile = async (mediaId: string) => {
  const { data } = await client.delete<CustomDocumentDataType>(`/v1/media/${mediaId}/project`, await withAuthConfig());
  return data;
};

// Drawings (OQ-161, flows/drawings.md §3).
export const createDrawing = async (orgId: string, body: DrawingRegistration): Promise<CustomDrawingDataType> => {
  const { data } = await client.post<CustomDrawingDataType>(`/v1/drawings/${orgId}`, body, await withAuthConfig());
  return data;
};

export const updateDrawing = async (
  orgId: string,
  drawingId: string,
  change: { name?: string; location?: { latitude: number; longitude: number }; projectId?: string | null }
): Promise<CustomDrawingDataType> => {
  const { data } = await client.patch<CustomDrawingDataType>(
    `/v1/drawings/${orgId}/${drawingId}`,
    change,
    await withAuthConfig()
  );
  return data;
};

export const deleteDrawing = async (orgId: string, drawingId: string): Promise<void> => {
  await client.delete(`/v1/drawings/${orgId}/${drawingId}`, await withAuthConfig());
};

// The report of a whole Drawing: the server stores it and answers a signed URL that downloads it as
// <name>-rapport.pdf (OQ-168, flows/drawings.md §6). A failure reaches the caller.
export const downloadDrawingReport = async (orgId: string, drawing: { docId: string; name: string }) => {
  const { data } = await client.get<{ url: string; expires: string }>(
    `/v1/drawings/${orgId}/${drawing.docId}/report`,
    await withAuthConfig()
  );
  const a = document.createElement('a');
  a.href = data.url;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const getAllStatuses = async (config?: AxiosRequestConfig) => {
  const { data } = await client.get<CustomStatusDataType[]>('/v1/status', await withAuthConfig(config));
  return data;
};

export const editStatus = async (status: Partial<StatusDataType>, id: string) => {
  const { data } = await client.put<CustomStatusDataType>(
    `/v1/status/${id}`,
    { status: { ...status } },
    await withAuthConfig()
  );
  return data;
};

export const createStatus = async (status: StatusDataType) => {
  const { data } = await client.post<CustomStatusDataType>(
    '/v1/status',
    { status: { ...status } },
    await withAuthConfig()
  );
  return data;
};

export const deleteStatus = async (id: string) => {
  const { data } = await client.delete(`/v1/status/${id}`, await withAuthConfig());
  return data;
};

export const uploadImageByOrgId = async (orgId: string, form: FormData) => {
  // For multipart/form-data, use auth but omit Content-Type (let axios set boundary automatically)
  const authHeader = await buildAuthHeader(true);
  const { data } = await client.post<CustomDocumentDataType>(`/v1/media/${orgId}`, form, {
    headers: {
      ...authHeader
      // Omit Content-Type to let axios set boundary automatically for FormData
      // If backend requires explicit multipart, uncomment:
      // 'Content-Type': 'multipart/form-data',
    }
  });
  return data;
};

export const reSendActivationEmail = async (user: UserDataType, id: string) => {
  const { data } = await client.post<CustomUserDataType>(
    `/v1/resendactivationemail/${id}`,
    { user: { ...user } },
    await withAuthConfig()
  );
  return data;
};

export const resetPassword = async (email: string) => {
  const { data } = await client.post<{ email: string; link: string }>(
    '/v1/password-reset',
    { email },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return data;
};
